import { randomid, replaceTags, isJSType, assign, trim, isNullish, isNotNullish, nativeElement } from '@noda-lib/utils/handlers';
import { BehaviorSubject, distinctUntilChanged, debounceTime, switchMap, map, take, merge } from 'rxjs';
import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { contentChild, Input, Directive, InjectionToken, input, inject, HostListener, signal, output, HostBinding, ViewEncapsulation, ChangeDetectionStrategy, Component, computed, viewChild, ViewContainerRef, TemplateRef, Host, effect, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { Bind } from '@noda-lib/utils/decorators';
import { ASSETS_LANG, ASSETS_LOCALIZE_FN } from '@noda-lib/assets/localize';
import * as i1$1 from '@angular/common';
import { CommonModule, AsyncPipe } from '@angular/common';
import { LetDirective } from '@noda-lib/utils/directives';
import * as i2$1 from '@noda-lib/utils/pipes';
import { EqualPipe, NotDefinedPipe, SanitizerPipe, MapPipe, DefinedPipe, ConditionPipe } from '@noda-lib/utils/pipes';
import { ANStyles } from '@noda-lib/utils/animations';
import * as i2 from '@noda-lib/assets/components';
import { IconComponent } from '@noda-lib/assets/components';
import { getProviderKey, getProviderKeyByType } from '@noda-lib/assets/tools';

/* eslint-disable no-useless-escape */
/**
 * Класс, содержащий статические методы для получения регулярных выражений, используемых для валидации форм.
 */
const _c0 = ["defaultContainerRef"];
const _c1 = ["messagesTpl"];
const _forTrack0 = ($index, $item) => $item.id;
function FormMessagesComponent_ng_template_2_For_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 3);
    i0.ɵɵpipe(1, "map");
    i0.ɵɵpipe(2, "sanitize");
  }
  if (rf & 2) {
    const message_r1 = ctx.$implicit;
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("innerHTML", i0.ɵɵpipeBind2(2, 5, i0.ɵɵpipeBind2(1, 2, message_r1.message, ctx_r1.mapFn()), "html"), i0.ɵɵsanitizeHtml);
    i0.ɵɵattribute("data-control-status", message_r1.type);
  }
}
function FormMessagesComponent_ng_template_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵrepeaterCreate(1, FormMessagesComponent_ng_template_2_For_2_Template, 3, 8, "span", 3, _forTrack0);
    i0.ɵɵpipe(3, "slice");
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("@ANShowMessages", undefined);
    i0.ɵɵadvance();
    i0.ɵɵrepeater(i0.ɵɵpipeBind3(3, 1, ctx_r1.messages(), 0, ctx_r1.limit()));
  }
}
const _c2 = ["afterControlContentRef"];
const _c3 = ["*"];
function AssetsControlComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 3);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r0.label(), " ");
  }
}
function AssetsControlComponent_Conditional_9_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "assets-form-messages", 6);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("container", ctx_r0.$overrideContainer())("mapFn", ctx_r0.mapFn())("messages", ctx_r0.messages());
  }
}
class FormPattern {
  /**
   * Регулярное выражение для проверки IBAN (международного номера банковского счета).
   *
   * @returns {RegExp}
   */
  static get IBAN() {
    return new RegExp(/^[a-z]{2}[a-z0-9*]+$/i);
  }
  /**
   * Регулярное выражение для проверки IBAN (международного номера банковского счета) на максимальное количество символов (42)
   *
   * @returns {RegExp}
   */
  static get IBAN_LENGTH() {
    return new RegExp(/^.{0,42}$/);
  }
  /**
   * Регулярное выражение для проверки, что значение состоит только из цифр.
   *
   * @returns {RegExp}
   */
  static get NUMBERS() {
    return new RegExp(/^[0-9]+$/);
  }
  /**
   * Регулярное выражение для проверки, что значение не содержит пробелов.
   *
   * @returns {RegExp}
   */
  static get NO_SPACES() {
    return new RegExp(/^\S+$/);
  }
  /**
   * Регулярное выражение для SWIFT
   *
   * @returns {RegExp}
   */
  static get SWIFT() {
    return new RegExp(/^[A-Z]{4}([A-Z]{2}){2}[0-9]{3}$/i);
  }
  /**
   * Регулярное выражение для HTTP URL
   *
   * @returns {RegExp}
   */
  static get HTTP_URL() {
    return new RegExp(/^(https?:\/\/)?([a-z0-9-]+[.]{1}){1,3}\w{2,10}$/i);
  }
  /**
   * Регулярное выражение для email
   *
   * @returns {RegExp}
   */
  static get EMAIL() {
    return new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
  }
}

/**
 * Класс, представляющий сообщение об ошибке формы.
 */
class FormError {
  /**
   * Создает экземпляр FormValueMessage.
   *
   * @param {string | html | TranslateKey} message Текст сообщения
   * @param {TagsMap} [params=null] Дополнительные теги сообщения, которые будут заменены c помощью функции replaceTags из библиотеки @noda-lib/utils/handlers (default: null)
   * @param {boolean} [skipTranslate=true] Флаг, указывающий, нужно ли пропускать перевод поля message, чтобы оставить строковое значение. Может использоваться при локализации, когда поле message является ключом перевода (default: true)
   */
  constructor(message, params, skipTranslate = true) {
    this.message = message;
    this.params = params;
    this.skipTranslate = skipTranslate;
  }
}
class FormMessage {
  constructor(type, message) {
    this.type = type;
    this.message = message;
    this.id = randomid();
  }
}

/**
 * Класс, представляющий сообщение о значении формы.
 *
 * @template T Тип значения сообщения (default: string).
 */
class FormValueMessage {
  /**
   * Создает экземпляр FormValueMessage.
   *
   * @param {TFormMessageType} type Тип сообщения.
   * @param {T = string} value Значение, при котором сообщение должно отображаться
   * @param {string | html | TranslateKey} message Текст сообщения
   * @param {TagsMap} [params=null] Дополнительные теги сообщения, которые будут заменены c помощью функции replaceTags из библиотеки @noda-lib/utils/handlers (default: null)
   * @param {boolean} [skipTranslate=true] Флаг, указывающий, нужно ли пропускать перевод поля message, чтобы оставить строковое значение. Может использоваться при локализации, когда поле message является ключом перевода (default: true)
   */
  constructor(type, value, message, params = null, skipTranslate = true) {
    this.type = type;
    this.value = value;
    this.message = message;
    this.params = params;
    this.skipTranslate = skipTranslate;
  }
}
function makeFormControlErrors(errors, errorsMap, messageHandler = replaceTags) {
  const messages = Object.entries(errors).reduce((accum, [errorKey, errorValue]) => {
    switch (true) {
      case isJSType(errorValue, 'string'):
        {
          accum.push(new FormMessage('error', errorValue));
          break;
        }
      case errorValue instanceof FormError:
        {
          accum.push(makeMessageFromErrorInstance(errorValue));
          break;
        }
      case Boolean(errorsMap[errorKey]):
        {
          accum.push(makeMessageFromValidationError(errorsMap[errorKey], errorValue, messageHandler));
          break;
        }
      default:
        {
          accum.push(new FormMessage('error', errorsMap['defaultInvalid']));
        }
    }
    return accum;
  }, []);
  return messages;
}
function makeMessageFromValidationError(msg, errorParams, messageHandler) {
  const tagsMap = assign(errorParams);
  const message = messageHandler(msg, tagsMap);
  return new FormMessage('error', message);
}
function makeMessageFromErrorInstance(error, messageHandler = replaceTags) {
  const {
    message: msg,
    params,
    skipTranslate
  } = error;
  const modificationHandler = skipTranslate ? replaceTags : messageHandler;
  const message = modificationHandler(msg, params);
  return new FormMessage('error', message);
}
function patchCompareValue(value) {
  switch (true) {
    case isJSType(value, 'string'):
      {
        return trim(value);
      }
    default:
      {
        return value;
      }
  }
}
function compareValues(aValue, bValue, compareFn = null) {
  const isExistCompareFn = Boolean(compareFn);
  switch (true) {
    case isExistCompareFn:
      {
        return compareFn(aValue, bValue);
      }
    default:
      {
        aValue = patchCompareValue(aValue);
        bValue = patchCompareValue(bValue);
        return Object.is(aValue, bValue);
      }
  }
}
class FormValidator {
  /**
   * Валидатор, который проверяет значение поля на соответствие заданному регулярному выражению.
   *
   * @param {TFormPattern} params Объект, содержащий регулярное выражение и сообщение об ошибке.
   * @param {string | RegExp} params.pattern Регулярное выражение для проверки значения.
   * @param {TValidationError} params.message Сообщение об ошибке, если значение не соответствует регулярному выражению.
   * @returns {ValidatorFn} Функция валидатора, возвращающая TValidationError, если значение НЕ соответствует регулярному выражению.
   */
  static pattern({
    pattern,
    message,
    optional = false
  }) {
    return function (control) {
      const {
        value: actualValue
      } = control;
      const isValidPattern = new RegExp(pattern).test(actualValue);
      const isOptional = optional && (isNullish(actualValue) || actualValue === '');
      const isValid = isValidPattern || isOptional;
      return isValid ? null : {
        pattern: message
      };
    };
  }
  /**
   * Валидатор, который проверяет, что значение поля не содержит пробелов.
   *
   * @param {AbstractControl} ctrl Контрол, значение которого нужно проверить.
   * @returns {{ excludeSpaces: true } | null} Объект ошибки валидации, если значение содержит пробелы, или null, если пробелов нет.
   */
  static excludeSpaces(ctrl) {
    const {
      value
    } = ctrl;
    return isNotNullish(value) && !~String(value).search(' ') ? null : {
      excludeSpaces: true
    };
  }
  /**
   * Асинхронный валидатор, который валидирует значение FormControl с задержкой (debounce).
   *
   * @template T Тип значения, которое будет возвращать целевой Observable
   * @param {TAsyncDebounceParams} request$ - Массив типа TAsyncDebounceParams.
   * @param {number} [ms=400] - Время задержки в миллисекундах перед выполнением запроса.
   * @returns {AsyncValidatorFn} Асинхронная функция валидатора, возвращающая Observable или Promise с результатами проверки.
   */
  static asyncDebounce([request$, errorChecker], ms = 400) {
    const sub$ = new BehaviorSubject(null);
    const errorCheckSub$ = sub$.pipe(distinctUntilChanged(), debounceTime(ms), switchMap(request$), map(errorChecker), take(1));
    return ctrl => {
      sub$.next(ctrl.value);
      return errorCheckSub$;
    };
  }
  /**
   * Валидатор, который проверяет значение поля на наличие ошибок, заданных в массиве ошибок.
   *
   * @template T Тип значения сообщения.
   * @param {Signal<FormValueMessage<T>[]>} errorsByValue - Сигнал, содержащий массив ошибок.
   * @param {(controlValue: unknown, errorValue: unknown) => boolean} [compareFn] - Функция, определяющая стратегию сравнения значений. По умолчанию null.
   * @returns {ValidatorFn} Функция валидатор, возвращающая {hasErrorByValue: FormError} | null
   * @throws {Error} Если тип ошибок не является массивом FormValueMessage.
   */
  static errorByValue(errorsByValue, compareFn = null) {
    return ({
      value
    }) => {
      const errors = errorsByValue();
      const isCheckErrors = Boolean(errors) && isNotNullish(value);
      if (isCheckErrors) {
        const isInvalidErrorsType = !Array.isArray(errors);
        if (isInvalidErrorsType) throw new Error('Invalid errors type. Errors should be FormValueMessage array');
        const error = errors.find(({
          value: errorValue
        }) => {
          return compareValues(value, errorValue, compareFn);
        });
        if (Boolean(error)) {
          const {
            message
          } = error;
          const {
            skipTranslate,
            params
          } = error;
          return {
            hasErrorByValue: new FormError(message, assign(params, {
              value
            }), skipTranslate)
          };
        }
      }
      return null;
    };
  }
}
let FormControlDisabledDirective = /*#__PURE__*/(() => {
  class FormControlDisabledDirective {
    constructor() {
      this.formControl = contentChild(NgControl);
    }
    set _disabled(value) {
      const method = value ? 'disable' : 'enable';
      queueMicrotask(() => {
        this.formControl().control[method]();
      });
    }
    static {
      this.ɵfac = function FormControlDisabledDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FormControlDisabledDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormControlDisabledDirective,
        selectors: [["", "assetsFormControlDisabled", ""]],
        contentQueries: function FormControlDisabledDirective_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuerySignal(dirIndex, ctx.formControl, NgControl, 5);
          }
          if (rf & 2) {
            i0.ɵɵqueryAdvance();
          }
        },
        inputs: {
          _disabled: [0, "assetsFormControlDisabled", "_disabled"]
        }
      });
    }
  }
  return FormControlDisabledDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const FORM_ERRORS_AVAILABLE_LANGUAGES = ['en', 'de', 'es', 'fr'];
const DefaultErrorMessages$4 = {
  required: 'Der Wert ist erforderlich',
  'not-equal-list-values': 'Der Wert muss aus der Liste ausgewählt werden',
  minlength: 'Der Wert darf nicht weniger als {{requiredLength}} Zeichen haben',
  maxlength: 'Der Wert darf nicht länger als {{requiredLength}} Zeichen sein',
  min: 'Der Wert darf nicht kleiner als {{min}} sein',
  max: 'Der Wert darf nicht größer als {{max}} sein',
  excludeSpaces: 'Der Wert darf keine Leerzeichen enthalten',
  'form-radio-required': 'Bitte wählen Sie eine Option',
  httpPattern: 'Der Wert muss dem URL-Typ entsprechen: "https://beispiel.com"',
  emailPattern: 'Das E-Mail-Format ist falsch',
  emailsPattern: 'Das Format einer der E-Mails ist falsch',
  ibanPattern: 'IBAN muss dem Typ entsprechen: "AA 12 3456 7890 1234 5678 90"',
  swiftPattern: 'SWIFT muss dem Typ entsprechen: "AAAA-BB-CC-123"',
  defaultInvalid: 'Der Wert ist ungültig'
};
const DefaultErrorMessages$3 = {
  required: 'The value is required',
  'not-equal-list-values': 'The value must be selected from the list',
  minlength: 'The value cannot be less than {{requiredLength}} characters',
  maxlength: 'The value cannot be longer than {{requiredLength}} characters',
  min: 'The value cannot be smaller than {{min}}',
  max: 'The value cannot be larger than {{max}}',
  excludeSpaces: 'The value must not contain spaces',
  'form-radio-required': 'Please select option',
  httpPattern: 'The value must match the URL type: "https://example.com"',
  emailPattern: 'Email format is incorrect',
  emailsPattern: 'The format of one of the emails is incorrect',
  ibanPattern: 'IBAN must match the type: "AA 12 3456 7890 1234 5678 90"',
  swiftPattern: 'SWIFT must match the type: "AAAA-BB-CC-123"',
  defaultInvalid: 'The value is invalid'
};
const DefaultErrorMessages$2 = {
  required: 'El valor es requerido',
  'not-equal-list-values': 'El valor debe ser seleccionado de la lista',
  minlength: 'El valor no puede tener menos de {{requiredLength}} caracteres',
  maxlength: 'El valor no puede tener más de {{requiredLength}} caracteres',
  min: 'El valor no puede ser menor que {{min}}',
  max: 'El valor no puede ser mayor que {{max}}',
  excludeSpaces: 'El valor no debe contener espacios',
  'form-radio-required': 'Por favor selecciona una opción',
  httpPattern: 'El valor debe coincidir con el tipo de URL: "https://ejemplo.com"',
  emailPattern: 'El formato del correo electrónico es incorrecto',
  emailsPattern: 'El formato de uno de los correos electrónicos es incorrecto',
  ibanPattern: 'El IBAN debe coincidir con el tipo: "AA 12 3456 7890 1234 5678 90"',
  swiftPattern: 'El SWIFT debe coincidir con el tipo: "AAAA-BB-CC-123"',
  defaultInvalid: 'El valor no es válido'
};
const DefaultErrorMessages$1 = {
  required: 'La valeur est requise',
  'not-equal-list-values': 'La valeur doit être sélectionnée dans la liste',
  minlength: 'La valeur ne peut pas avoir moins de {{requiredLength}} caractères',
  maxlength: 'La valeur ne peut pas avoir plus de {{requiredLength}} caractères',
  min: 'La valeur ne peut pas être inférieure à {{min}}',
  max: 'La valeur ne peut pas être supérieure à {{max}}',
  excludeSpaces: `La valeur ne doit pas contenir d'espaces`,
  'form-radio-required': 'Veuillez sélectionner une option',
  httpPattern: `La valeur doit correspondre au type d'URL : "https://exemple.com"`,
  emailPattern: `Le format de l'e-mail est incorrect`,
  emailsPattern: `Le format d'un des e-mails est incorrect`,
  ibanPattern: `L'IBAN doit correspondre au type : "AA 12 3456 7890 1234 5678 90"`,
  swiftPattern: 'Le code SWIFT doit correspondre au type : "AAAA-BB-CC-123"',
  defaultInvalid: "La valeur n'est pas valide"
};
const DefaultErrorMessages = {
  en: DefaultErrorMessages$3,
  de: DefaultErrorMessages$4,
  fr: DefaultErrorMessages$1,
  es: DefaultErrorMessages$2
};
function defaultErrorStrategyFn(formControl) {
  const {
    invalid,
    touched,
    dirty,
    pristine,
    disabled
  } = formControl;
  return invalid && touched && dirty && !pristine && !disabled;
}

// * Exports
const ERROR_STRATEGY_FN = new InjectionToken('ERROR_STRATEGY_FN', {
  factory() {
    return defaultErrorStrategyFn;
  }
});
const ERROR_MESSAGES_MAP = new InjectionToken('ERROR_MESSAGES_MAP');
const ERROR_MESSAGES_OVERRIDE_MAP = new InjectionToken('ERROR_MESSAGES_OVERRIDE_MAP', {
  factory() {
    return {};
  }
});
const ERROR_MESSAGES_COMPONENT_MAP = new InjectionToken('ERROR_MESSAGES_COMPONENT_MAP', {
  factory() {
    return {};
  }
});
let FormGroupErrorsStrategyPage = /*#__PURE__*/(() => {
  class FormGroupErrorsStrategyPage {
    constructor() {
      this.strategy = input.required({
        alias: 'errorsFormGroupStrategy'
      });
    }
    static {
      this.ɵfac = function FormGroupErrorsStrategyPage_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FormGroupErrorsStrategyPage)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormGroupErrorsStrategyPage,
        selectors: [["", "errorsFormGroupStrategy", ""]],
        inputs: {
          strategy: [1, "errorsFormGroupStrategy", "strategy"]
        }
      });
    }
  }
  return FormGroupErrorsStrategyPage;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FormErrorMessagesDirective = /*#__PURE__*/(() => {
  class FormErrorMessagesDirective {
    constructor() {
      // * Inject
      this.lang = inject(ASSETS_LANG);
      this.errorsComponentStrategy = inject(ERROR_STRATEGY_FN);
      this.errorsFormGroupStrategy = inject(FormGroupErrorsStrategyPage, {
        optional: true
      });
      this.errorMessagesMap = inject(ERROR_MESSAGES_MAP, {
        optional: true
      });
      this.errorsRootOverrideMap = inject(ERROR_MESSAGES_OVERRIDE_MAP);
      this.errorsComponentOverrideMap = inject(ERROR_MESSAGES_COMPONENT_MAP);
      this.localizeFn = inject(ASSETS_LOCALIZE_FN);
      // * Inputs
      this.rootOverrideModule = input(null, {
        alias: 'errorsOverrideModule'
      });
      this.componentOverrideModule = input(null, {
        alias: 'errorsComponentModule'
      });
      this.errorsFormControlStrategy = input(null, {
        alias: 'errorsFormControlStrategy'
      });
      // * Children
      this.ngControl = contentChild(NgControl, {
        read: NgControl
      });
    }
    onFocusChange() {
      this.ngControl().control.updateValueAndValidity();
    }
    canShowErrors(formControl) {
      return (this.errorsFormControlStrategy()?.(formControl, this.errorsComponentStrategy) ?? this.errorsFormGroupStrategy?.strategy()(formControl, this.errorsComponentStrategy) ?? this.errorsComponentStrategy(formControl)) && Boolean(formControl.errors);
    }
    makeFormControlErrors(controlErrors) {
      const errorMessagesMap = this.getErrorMessagesMap();
      const errors = Boolean(controlErrors) ? makeFormControlErrors(controlErrors, errorMessagesMap, this.localizeFn) : null;
      return errors;
    }
    get availableLanguage() {
      const tokenLanguage = this.lang?.language;
      const isHasLanguage = FORM_ERRORS_AVAILABLE_LANGUAGES.includes(tokenLanguage);
      const language = isHasLanguage ? tokenLanguage : 'en';
      return language;
    }
    getErrorMessagesMap() {
      return assign(DefaultErrorMessages[this.availableLanguage], this.errorMessagesMap, this.errorsRootOverrideMap[this.rootOverrideModule()], this.errorsComponentOverrideMap[this.componentOverrideModule()]);
    }
    static {
      this.ɵfac = function FormErrorMessagesDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FormErrorMessagesDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormErrorMessagesDirective,
        selectors: [["", "assetsFormErrors", ""]],
        contentQueries: function FormErrorMessagesDirective_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuerySignal(dirIndex, ctx.ngControl, NgControl, 5, NgControl);
          }
          if (rf & 2) {
            i0.ɵɵqueryAdvance();
          }
        },
        hostBindings: function FormErrorMessagesDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("focusout", function FormErrorMessagesDirective_focusout_HostBindingHandler() {
              return ctx.onFocusChange();
            });
          }
        },
        inputs: {
          rootOverrideModule: [1, "errorsOverrideModule", "rootOverrideModule"],
          componentOverrideModule: [1, "errorsComponentModule", "componentOverrideModule"],
          errorsFormControlStrategy: [1, "errorsFormControlStrategy"]
        }
      });
    }
  }
  return FormErrorMessagesDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FormGroupDisabledDirective = /*#__PURE__*/(() => {
  class FormGroupDisabledDirective {
    set _disabled(value) {
      const method = value ? 'disable' : 'enable';
      queueMicrotask(() => {
        this.formGroup.form[method]();
      });
    }
    constructor(formGroup) {
      this.formGroup = formGroup;
    }
    static {
      this.ɵfac = function FormGroupDisabledDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FormGroupDisabledDirective)(i0.ɵɵdirectiveInject(i1.FormGroupDirective));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormGroupDisabledDirective,
        selectors: [["", "assetsFormDisabled", ""]],
        inputs: {
          _disabled: [0, "assetsFormDisabled", "_disabled"]
        }
      });
    }
  }
  return FormGroupDisabledDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FormPlaceholderDirective = /*#__PURE__*/(() => {
  class FormPlaceholderDirective {
    set placeholder(value) {
      this._placeholder = value;
      queueMicrotask(() => this.setPlaceholder());
    }
    constructor(elementRef) {
      this.elementRef = elementRef;
    }
    setPlaceholder() {
      const nativeEl = this.getNativeFocusableElement();
      if (nativeEl) {
        nativeEl.placeholder = this._placeholder;
      }
    }
    getNativeFocusableElement() {
      const el = nativeElement(this.elementRef);
      return el.querySelector('input, textarea') || el;
    }
    static {
      this.ɵfac = function FormPlaceholderDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FormPlaceholderDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormPlaceholderDirective,
        selectors: [["", "assetsFormPlaceholder", ""]],
        inputs: {
          placeholder: [0, "assetsFormPlaceholder", "placeholder"]
        }
      });
    }
  }
  return FormPlaceholderDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class FormValueMessagesDirective {
  constructor() {
    this.computeStatus = () => {};
    // * Inject
    this.localizeFn = inject(ASSETS_LOCALIZE_FN);
    // * Inputs
    this.messagesMap = input(new Map(), {
      alias: 'messagesByValue',
      transform: this.transformMessagesByValue
    });
  }
  // * Children
  transformMessagesByValue(messages) {
    const messagesMap = new Map();
    if (Boolean(messages)) {
      messages.reduce((accum, valueMessage) => {
        const {
          type,
          message,
          skipTranslate,
          value,
          params
        } = valueMessage;
        const messageText = skipTranslate ? replaceTags(message, assign(params)) : this.localizeFn(value, params);
        const formMessage = new FormMessage(type, messageText);
        const key = JSON.stringify(value);
        messagesMap.set(key, formMessage);
        return accum;
      }, messagesMap);
    }
    queueMicrotask(this.computeStatus);
    return messagesMap;
  }
  getMessageByValue(value) {
    if (Array.isArray(value)) {
      const key = value.find(v => this.isMessageMapHasValue(v));
      if (key) {
        return this.getMessageFromMessageMap(key);
      }
    }
    return this.getMessageFromMessageMap(value);
  }
  isExistMessageByValue(value) {
    if (Array.isArray(value)) {
      return value.some(v => this.isMessageMapHasValue(v));
    }
    return this.isMessageMapHasValue(value);
  }
  isMessageMapHasValue(value) {
    return this.messagesMap().has(JSON.stringify(value));
  }
  getMessageFromMessageMap(value) {
    return this.messagesMap().get(JSON.stringify(value));
  }
  static {
    this.ɵfac = function FormValueMessagesDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FormValueMessagesDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: FormValueMessagesDirective,
      selectors: [["", "assetsFormValueMessages", ""]],
      inputs: {
        messagesMap: [1, "messagesByValue", "messagesMap"]
      },
      exportAs: ["formMessagesRef"]
    });
  }
}
__decorate([Bind()], FormValueMessagesDirective.prototype, "transformMessagesByValue", null);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const FormInputSubmitImports = [CommonModule, IconComponent, EqualPipe, NotDefinedPipe, AsyncPipe];
class FormInputSubmitComponent {
  constructor() {
    this.ngControl = null;
    this.disabled = signal(false);
    this.next = output();
  }
  isDisabledStatus(status) {
    return status === 'DISABLED' || status === 'INVALID';
  }
  static {
    this.ɵfac = function FormInputSubmitComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FormInputSubmitComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: FormInputSubmitComponent,
      selectors: [["assets-form-input-submit"]],
      hostVars: 1,
      hostBindings: function FormInputSubmitComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵsyntheticHostProperty("@ANShowBtn", ctx.showBtnAnimation);
        }
      },
      outputs: {
        next: "next"
      },
      decls: 5,
      vars: 3,
      consts: [[1, "assets-input-submit-btn", 3, "click"], ["icon", "arrow_forward", 1, "assets-input-submit-btn-icon"]],
      template: function FormInputSubmitComponent_Template(rf, ctx) {
        if (rf & 1) {
          const _r1 = i0.ɵɵgetCurrentView();
          i0.ɵɵdeclareLet(0);
          i0.ɵɵpipe(1, "async");
          i0.ɵɵdeclareLet(2);
          i0.ɵɵelementStart(3, "button", 0);
          i0.ɵɵlistener("click", function FormInputSubmitComponent_Template_button_click_3_listener() {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx.next.emit());
          });
          i0.ɵɵelement(4, "assets-icon", 1);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          let tmp_0_0;
          let tmp_1_0;
          const status_r2 = i0.ɵɵpipeBind1(1, 1, (tmp_0_0 = ctx.ngControl()) == null ? null : tmp_0_0.statusChanges) || ((tmp_0_0 = ctx.ngControl()) == null ? null : tmp_0_0.status);
          const isDisabled_r3 = !((tmp_1_0 = ctx.ngControl()) == null ? null : tmp_1_0.pristine) && ctx.isDisabledStatus(status_r2) || ctx.disabled();
          i0.ɵɵadvance(3);
          i0.ɵɵattribute("data-btn-disabled", isDisabled_r3);
        }
      },
      dependencies: [i1$1.CommonModule, i1$1.AsyncPipe, i2.IconComponent],
      styles: [".assets-input-submit-btn{--assets-icon-font-size: 24px;display:flex;height:100%;padding:0;padding-inline:16px;color:var(--assets-base-01);background-color:var(--assets-primary);border-radius:0 var(--assets-radius-l) var(--assets-radius-l) 0;transition:background-color var(--assets-animation)}.assets-input-submit-btn[data-btn-disabled=true]{pointer-events:none;cursor:default;opacity:.48;background-color:var(--assets-primary-hover)}.assets-input-submit-btn[data-btn-disabled=true]:hover,.assets-input-submit-btn[data-btn-disabled=true]:active{background-color:var(--assets-primary-hover)}.assets-input-submit-btn:not([data-btn-disabled=true]):hover{background-color:var(--assets-primary-hover)!important}.assets-input-submit-btn-icon{margin:auto;color:var(--assets-base-stable-white)}[data-control-status=default]:hover .assets-input-submit-btn:not([data-btn-disabled=true]){background-color:var(--assets-link-hover)}[data-control-status=default]:focus-within .assets-input-submit-btn:not([data-btn-disabled=true]){background-color:var(--assets-primary)}.assets-control:has(.assets-input-submit-btn) [automation-id=tui-primitive-textfield__wrapper]{border-top-right-radius:0;border-bottom-right-radius:0}\n"],
      encapsulation: 2,
      data: {
        animation: [ANStyles({
          animationName: 'ANShowBtn',
          fromStyle: {
            opacity: '0',
            width: '0'
          },
          toStyle: {
            opacity: '1',
            width: '*'
          }
        })]
      },
      changeDetection: 0
    });
  }
}
__decorate([Bind()], FormInputSubmitComponent.prototype, "isDisabledStatus", null);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const FormMessagesImports = [CommonModule, SanitizerPipe, MapPipe];
let FormMessagesComponent = /*#__PURE__*/(() => {
  class FormMessagesComponent {
    constructor() {
      // * Props
      this.$container = computed(() => this.$outsideContainerRef() || this.$defaultContainerRef());
      // * Inputs
      this.messages = input.required();
      this.mapFn = input(null);
      this.limit = input(1);
      this.$outsideContainerRef = input(null, {
        alias: 'container'
      });
      // * Children
      this.$defaultContainerRef = viewChild('defaultContainerRef', {
        read: ViewContainerRef
      });
      this.$messagesTpl = viewChild('messagesTpl', {
        read: TemplateRef
      });
    }
    ngOnInit() {
      this.$container().createEmbeddedView(this.$messagesTpl());
    }
    ngOnDestroy() {
      this.$container().clear();
    }
    static {
      this.ɵfac = function FormMessagesComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FormMessagesComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: FormMessagesComponent,
        selectors: [["assets-form-messages"]],
        viewQuery: function FormMessagesComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuerySignal(ctx.$defaultContainerRef, _c0, 5, ViewContainerRef);
            i0.ɵɵviewQuerySignal(ctx.$messagesTpl, _c1, 5, TemplateRef);
          }
          if (rf & 2) {
            i0.ɵɵqueryAdvance(2);
          }
        },
        hostVars: 1,
        hostBindings: function FormMessagesComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵsyntheticHostProperty("@ANShowMessages", ctx.showMsgsAnimation);
          }
        },
        inputs: {
          messages: [1, "messages"],
          mapFn: [1, "mapFn"],
          limit: [1, "limit"],
          $outsideContainerRef: [1, "container", "$outsideContainerRef"]
        },
        decls: 4,
        vars: 0,
        consts: [["defaultContainerRef", ""], ["messagesTpl", ""], [1, "assets-form-messages"], [1, "assets-form-messages-item", "assets-text-body-m", 3, "innerHTML"]],
        template: function FormMessagesComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementContainer(0, null, 0);
            i0.ɵɵtemplate(2, FormMessagesComponent_ng_template_2_Template, 4, 5, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor);
          }
        },
        dependencies: [i1$1.CommonModule, i1$1.SlicePipe, i2$1.SanitizerPipe, i2$1.MapPipe],
        styles: ["[_nghost-%COMP%]{display:block}.assets-form-messages[_ngcontent-%COMP%]{overflow:hidden;display:flex;flex-direction:column;gap:5px;margin-top:5px}.assets-form-messages-item[_ngcontent-%COMP%]{color:var(--assets-main-status-color)}"],
        data: {
          animation: [ANStyles({
            animationName: 'ANShowMessages',
            fromStyle: {
              height: '0px',
              opacity: '0'
            },
            toStyle: {
              height: '*',
              opacity: '1'
            }
          })]
        },
        changeDetection: 0
      });
    }
  }
  return FormMessagesComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const AssetsControlImports = [CommonModule, FormInputSubmitComponent, FormMessagesComponent, LetDirective, DefinedPipe, ConditionPipe];
let AssetsControlComponent = class AssetsControlComponent {
  constructor(errorsMsgsDirective, valueMsgsDirective, cdr) {
    this.errorsMsgsDirective = errorsMsgsDirective;
    this.valueMsgsDirective = valueMsgsDirective;
    this.cdr = cdr;
    // * Props
    this.status = signal('default');
    this.messages = signal(null);
    // * Inputs
    this.label = input(null);
    this.modifier = input('stroke');
    this.mapFn = input(null, {
      alias: 'messagesMapFn'
    });
    this.$overrideContainer = input(null, {
      alias: 'messagesContainer'
    });
    // *Children
    this.ngControl = contentChild(NgControl, {
      read: NgControl
    });
    this.$afterControlContentRef = viewChild('afterControlContentRef', {
      read: ViewContainerRef
    });
  }
  ngOnInit() {
    this.provideHandleStatus();
  }
  ngAfterViewInit() {
    this.initChangesSubs();
    this.computeStatus();
  }
  provideHandleStatus() {
    // ! Если будет расширение, то сделать через токен доступа и автоматизировать через цикл
    this.valueMsgsDirective.computeStatus = this.computeStatus;
  }
  initChangesSubs() {
    const {
      control
    } = this.ngControl();
    merge(control.statusChanges.pipe(distinctUntilChanged()), control.valueChanges).pipe(untilDestroyed(this)).subscribe(this.computeStatus);
  }
  // eslint-disable-next-line @typescript-eslint/member-ordering
  computeStatus() {
    const {
      disabled,
      errors,
      value
    } = this.ngControl().control;
    const canShowErrorMessages = this.errorsMsgsDirective.canShowErrors(this.ngControl());
    const canShowValueMessages = this.valueMsgsDirective.isExistMessageByValue(value);
    switch (true) {
      case disabled:
        {
          this.handleDisabledStatus();
          break;
        }
      case canShowErrorMessages:
        {
          this.handleErrorStatus(errors);
          break;
        }
      case canShowValueMessages:
        {
          this.handleCustomStatus(value);
          break;
        }
      default:
        {
          this.handleDefaultStatus();
        }
    }
    this.cdr.detectChanges();
  }
  handleDisabledStatus() {
    this.status.set('disabled');
    this.messages.set(null);
  }
  handleErrorStatus(formErrors) {
    const errors = this.errorsMsgsDirective.makeFormControlErrors(formErrors);
    this.status.set('error');
    this.messages.set(errors);
  }
  handleCustomStatus(value) {
    const message = this.valueMsgsDirective.getMessageByValue(value);
    this.status.set(message.type);
    this.messages.set([message]);
  }
  handleDefaultStatus() {
    this.status.set('default');
    this.messages.set(null);
  }
  static {
    this.ɵfac = function AssetsControlComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || AssetsControlComponent)(i0.ɵɵdirectiveInject(FormErrorMessagesDirective, 1), i0.ɵɵdirectiveInject(FormValueMessagesDirective, 1), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: AssetsControlComponent,
      selectors: [["assets-control"]],
      contentQueries: function AssetsControlComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuerySignal(dirIndex, ctx.ngControl, NgControl, 5, NgControl);
        }
        if (rf & 2) {
          i0.ɵɵqueryAdvance();
        }
      },
      viewQuery: function AssetsControlComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuerySignal(ctx.$afterControlContentRef, _c2, 5, ViewContainerRef);
        }
        if (rf & 2) {
          i0.ɵɵqueryAdvance();
        }
      },
      inputs: {
        label: [1, "label"],
        modifier: [1, "modifier"],
        mapFn: [1, "messagesMapFn", "mapFn"],
        $overrideContainer: [1, "messagesContainer", "$overrideContainer"]
      },
      features: [i0.ɵɵHostDirectivesFeature([{
        directive: FormValueMessagesDirective,
        inputs: ["messagesByValue", "messagesByValue"]
      }, {
        directive: FormErrorMessagesDirective,
        inputs: ["errorsOverrideModule", "errorsOverrideModule", "errorsComponentModule", "errorsComponentModule", "errorsFormControlStrategy", "errorsFormControlStrategy"]
      }, {
        directive: FormControlDisabledDirective,
        inputs: ["assetsFormControlDisabled", "disabled"]
      }])],
      ngContentSelectors: _c3,
      decls: 10,
      vars: 6,
      consts: [["afterControlContentRef", ""], [1, "assets-control"], [1, "assets-control-wrapper"], [1, "assets-control-label"], [1, "assets-control-content"], [1, "assets-control-content-box"], [3, "container", "mapFn", "messages"]],
      template: function AssetsControlComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "div", 1)(1, "div", 2);
          i0.ɵɵtemplate(2, AssetsControlComponent_Conditional_2_Template, 2, 1, "span", 3);
          i0.ɵɵpipe(3, "defined");
          i0.ɵɵelementStart(4, "div", 4)(5, "div", 5);
          i0.ɵɵprojection(6);
          i0.ɵɵelementEnd();
          i0.ɵɵelementContainer(7, null, 0);
          i0.ɵɵelementEnd()();
          i0.ɵɵtemplate(9, AssetsControlComponent_Conditional_9_Template, 1, 3, "assets-form-messages", 6);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵattribute("data-control-modifier", ctx.modifier())("data-control-status", ctx.status());
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(i0.ɵɵpipeBind1(3, 4, ctx.label()) ? 2 : -1);
          i0.ɵɵadvance(7);
          i0.ɵɵconditional(ctx.messages() ? 9 : -1);
        }
      },
      dependencies: [i1$1.CommonModule, FormMessagesComponent, i2$1.DefinedPipe],
      styles: ["[class*=assets-text-service-h]{letter-spacing:-.02em}.assets-text-service-h1{font:var(--assets-font-service-h1)}.assets-text-service-h1-mob{font:var(--assets-font-service-h1-mob)}.assets-text-service-h2{font:var(--assets-font-service-h2)}.assets-text-service-h2-mob{font:var(--assets-font-service-h2-mob)}.assets-text-service-h3{font:var(--assets-font-service-h3)}.assets-text-service-h3-mob{font:var(--assets-font-service-h3-mob)}.assets-text-h1{font:var(--assets-font-heading-1);letter-spacing:-.02em}.assets-text-h2{font:var(--assets-font-heading-2);letter-spacing:-.02em}.assets-text-h3{font:var(--assets-font-heading-3);letter-spacing:-.01em}.assets-text-h4{font:var(--assets-font-heading-4);letter-spacing:-.01em}.assets-text-h5{font:var(--assets-font-heading-5)}.assets-text-h6{font:var(--assets-font-heading-6)}.assets-text-card-heading{font:var(--assets-font-card-heading)}.assets-text-link{cursor:pointer;color:var(--assets-link);text-decoration:underline}.assets-text-link:hover:not(.assets-text-disabled){color:var(--assets-link-hover)}.assets-text-label,assets-control.checkbox-label .assets-control-label{font:var(--assets-font-text-l);color:var(--assets-text-01);transition:color var(--assets-animation)}.assets-text-label.disabled,assets-control.checkbox-label .disabled.assets-control-label{color:var(--assets-text-02)}.assets-text-disabled{cursor:default;color:var(--assets-text-03)}.assets-text-error{color:var(--assets-error-fill)}.assets-text-warning{color:var(--assets-accent)}.assets-text-success{color:var(--assets-success-fill)}.assets-text-primary{color:var(--assets-primary)}.assets-text-body-xl-bold{font:var(--assets-font-text-xl-bold)}.assets-text-body-xl{font:var(--assets-font-text-xl)}.assets-text-body-l-bold{font:var(--assets-font-text-l-bold)}.assets-text-body-l-medium{font:var(--assets-font-text-l-medium)}.assets-text-body-l{font:var(--assets-font-text-l)}.assets-text-body-m-bold{font:var(--assets-font-text-m-bold)}.assets-text-body-m-medium{font:var(--assets-font-text-m-medium)}.assets-text-body-m{font:var(--assets-font-text-m)}.assets-text-body-s-bold{font:var(--assets-font-text-s-bold)}.assets-text-body-s{font:var(--assets-font-text-s)}.assets-text-body-s-bold-short-line{font:var(--assets-font-text-s-bold);line-height:110%}.assets-text-body-s-short-line{font:var(--assets-font-text-s);line-height:110%}.assets-text-body.disabled{color:var(--assets-text-03)}assets-control:has(:is([tuiRadio],[tuiCheckbox],[tuiSwitch])[data-size=s]){--assets-label-gap: 8px}assets-control:has(:is([tuiRadio],[tuiCheckbox],[tuiSwitch])[data-size=s]) .assets-control-label{font:var(--assets-font-text-m)}assets-control.checkbox-label .assets-control-wrapper{flex-direction:row;gap:var(--assets-label-gap, var(--assets-label-default-gap));align-items:center}assets-control.checkbox-label .assets-control-wrapper .assets-control-label{order:1}assets-control.checkbox-label .assets-control-wrapper .assets-control-content{display:flex;flex-grow:initial}assets-control.label-top .assets-control-wrapper,assets-control.label-bottom .assets-control-wrapper{flex-direction:column;align-items:flex-start}assets-control.label-left .assets-control-label,assets-control.label-top .assets-control-label{order:0!important}assets-control.label-left .assets-control-wrapper,assets-control.label-right .assets-control-wrapper{flex-direction:row;align-items:center}assets-control.label-bottom .assets-control-label,assets-control.label-right .assets-control-label{order:1}.assets-control .assets-control-wrapper{display:flex;flex-direction:column;gap:4px}.assets-control-label{font:var(--assets-font-text-m);color:var(--assets-text-02)}.assets-control[data-control-status=disabled] .assets-control-label{color:var(--assets-text-02)}.assets-control-content{display:flex;flex-grow:1}.assets-control-content-box{flex-grow:1}\n"],
      encapsulation: 2,
      changeDetection: 0
    });
  }
};
__decorate([Bind()], AssetsControlComponent.prototype, "computeStatus", null);
AssetsControlComponent = __decorate([UntilDestroy()], AssetsControlComponent);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/* eslint-disable @angular-eslint/directive-selector */
let FormSubmitButtonDirective = /*#__PURE__*/(() => {
  class FormSubmitButtonDirective {
    constructor() {
      // * Inject
      this.$assetsControl = inject(AssetsControlComponent, {
        host: true,
        optional: true
      });
      // * Input
      this.showSubmitBtn = input(false);
      this.showSubmitBtnEffect = effect(() => {
        this.showSubmitBtn() ? this.insertButton() : this.removeButton();
      });
      this.disabled = input(false, {
        alias: 'submitBtnDisabled'
      });
      // * Output
      this.next = output({
        alias: 'submitBtnNext'
      });
    }
    insertButton() {
      if (this.$instance) this.removeButton();
      this.$instance = this.$assetsControl.$afterControlContentRef().createComponent(FormInputSubmitComponent);
      this.$instance.instance.disabled = this.disabled;
      this.$instance.instance.ngControl = this.$assetsControl.ngControl;
      this.$instance.instance.next = this.next;
    }
    removeButton() {
      // ? Если потребуется расширение функционала для контейнера, то реализовать вставку через insert
      this.$assetsControl.$afterControlContentRef().clear();
      this.$instance?.destroy();
      this.$instance = null;
    }
    static {
      this.ɵfac = function FormSubmitButtonDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FormSubmitButtonDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormSubmitButtonDirective,
        selectors: [["", "showSubmitBtn", ""]],
        inputs: {
          showSubmitBtn: [1, "showSubmitBtn"],
          disabled: [1, "submitBtnDisabled", "disabled"]
        },
        outputs: {
          next: "submitBtnNext"
        }
      });
    }
  }
  return FormSubmitButtonDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const imports = [FormsModule, ReactiveFormsModule, AssetsControlComponent];
let AssetsFormModule = /*#__PURE__*/(() => {
  class AssetsFormModule {
    static {
      this.ɵfac = function AssetsFormModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AssetsFormModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AssetsFormModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [imports, FormsModule, ReactiveFormsModule]
      });
    }
  }
  return AssetsFormModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function provideFormErrorsMap(messagesMap) {
  const providerKey = getProviderKey(messagesMap);
  return [{
    provide: ERROR_MESSAGES_MAP,
    [providerKey]: messagesMap
  }];
}
function provideFormErrorsOverrideMap(messagesMap) {
  const providerKey = getProviderKey(messagesMap);
  return [{
    provide: ERROR_MESSAGES_OVERRIDE_MAP,
    [providerKey]: messagesMap
  }];
}
function provideFormErrorsComponentMap(messagesMap) {
  const providerKey = getProviderKey(messagesMap);
  return [{
    provide: ERROR_MESSAGES_COMPONENT_MAP,
    [providerKey]: messagesMap
  }];
}
function provideFormErrorsStrategy(fn, type = 'value') {
  const providerKey = getProviderKeyByType(type);
  return {
    provide: ERROR_STRATEGY_FN,
    [providerKey]: fn
  };
}

/**
 * Generated bundle index. Do not edit.
 */

export { AssetsControlComponent, AssetsFormModule, ERROR_MESSAGES_COMPONENT_MAP, ERROR_MESSAGES_MAP, ERROR_MESSAGES_OVERRIDE_MAP, ERROR_STRATEGY_FN, FormControlDisabledDirective, FormError, FormErrorMessagesDirective, FormGroupDisabledDirective, FormGroupErrorsStrategyPage, FormInputSubmitComponent, FormMessage, FormMessagesComponent, FormPattern, FormPlaceholderDirective, FormSubmitButtonDirective, FormValidator, FormValueMessage, FormValueMessagesDirective, defaultErrorStrategyFn, provideFormErrorsComponentMap, provideFormErrorsMap, provideFormErrorsOverrideMap, provideFormErrorsStrategy };
